import { Suspense } from 'react';

import { Navigate, Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { AppLayout } from '@/components/layouts/AppLayout';
import { AuthenticatedOnlyRoute } from '@/components/routes/AuthenticatedOnlyRoute';
import { PersonalSettingsTabRedirect } from '@/components/routes/PersonalRedirect';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// /settings/* — user-level settings. Requires auth but NOT org selection
// (GW-452), so this block carries its own AuthenticatedOnlyRoute + AppLayout
// scaffold instead of mounting under the ProtectedRoute tree. Mounted
// directly under RouteErrorBoundary in App.tsx.
// ============================================================================

const SettingsPage = lazyWithRetry(() =>
  import('@/pages/protected/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
// GW-8058: Consultant profile editor lives under /settings (target IA, GW-8043/D11)
const ConsultantProfilePage = lazyWithRetry(() =>
  import('@/pages/settings/ConsultantProfilePage').then((m) => ({
    default: m.ConsultantProfilePage,
  }))
);

export const settingsRoutes = (
  <>
    {/* GW-452: Personal settings routes - require auth only, no org selection */}
    {/* Personal settings are user-level (not org-level) and should be accessible */}
    {/* before organization selection to access GDPR features (data export, account deletion) */}
    {/* GW-8027 Phase 3: nested in its own <AppLayout /> (still under
        AuthenticatedOnlyRoute only, NOT ProtectedRoute/TOSGuard, to
        preserve the no-org-selection-required guard from GW-452)
        rather than self-composing via useAppChromeSlots — this
        mounts the same shared Sidebar.tsx chrome as every other
        protected route instead of the divergent SidebarShell one. */}
    {/* GW-8263: /settings/{tab} is now the canonical route (flipped from
        /personal/settings/{tab}, target IA §1.2/§6, 07-ISSUE-001). The
        legacy /personal/settings/{tab} path forward-redirects here —
        no back-compat alias is kept per the PRP's "no back-compat
        routing" standing decision. */}
    <Route element={<AuthenticatedOnlyRoute />}>
      {/* GW-10434: /settings/* renders through the unified AppLayout shell's
          DEFAULT authed variant (explicit for legibility with the sibling
          slices). Still under AuthenticatedOnlyRoute only — no org-selection
          gate (GW-452). */}
      <Route element={<AppLayout variant="default" />}>
        {/* GW-12256: bare /settings redirects to its default tab (Profile),
            matching the /organization -> /organization/summary and
            /publisher -> /publisher/summary section-root behavior. The
            /settings/profile route below (via /settings/:tab) renders
            SettingsPage with the profile tab, and the breadcrumb config now
            carries a /settings/profile leaf so the trail reads
            "Home > Settings > Profile". */}
        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
        <Route
          path="/settings/:tab"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SettingsPage />
            </Suspense>
          }
        />
        {/* GW-8058: Consultant profile editor — target IA settings tab
            (GW-8043/D11), replaces /consultant/profile */}
        <Route
          path="/settings/consultant-profile"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <ConsultantProfilePage />
            </Suspense>
          }
        />
      </Route>
      {/* GW-8263: Forward redirects from the retired /personal/settings/*
          path to the canonical /settings/* route. */}
      <Route path="/personal/settings/:tab" element={<PersonalSettingsTabRedirect />} />
      <Route path="/personal/settings" element={<Navigate to="/settings" replace />} />
    </Route>
  </>
);
